import { useEngineBus } from '../hooks/useEngineBus.js';

const SEGMENTS = 12;

/**
 * @file The health readout — a GTA-style slab that docks ABOVE the minimap in
 * the bottom-left cluster. Segmented bar plus a big display-font numeral, which
 * is the split that matters: the segments are what you read with peripheral
 * vision mid-fight, the numeral is what you read when you have a second.
 *
 * ARMOUR is a thin strip ABOVE the segments, and it is only drawn when there is
 * any. That is deliberate: armour is a thing you buy at a gunsmith's and then
 * spend (`systems/Shops.js` → `Player#hurt`), so an empty strip sitting there
 * permanently would read as a second health bar that is always at zero. It is
 * a CONTINUOUS bar rather than twelve segments, in cold steel-blue rather than
 * the health tones — two different shapes and two different temperatures, so a
 * glance can never mistake one reading for the other. It shares the health
 * payload because it is the same reading and a second channel would need a
 * second change gate that could disagree with the first.
 *
 * ⚠ This component does NOT position itself. The bottom-left cluster in
 * `routes/PlayRoute.jsx` owns the layout, because health and the minimap have
 * to share one column and one width — a self-positioning health bar and a
 * self-positioning minimap drift apart the moment either changes size. It
 * still returns `null` until the first `'player:hp'` (a dev scene with no
 * player has no health), and the cluster's flex column closes the gap.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 * @param {number} [props.width=220] CSS px — match the minimap.
 */
export default function HealthBar({ engine, width = 220 }) {
  const hp = useEngineBus(engine, 'player:hp', { throttle: 100 });
  if (!hp) return null;

  const max = Math.max(1, Number(hp.max) || 100);
  const current = Math.max(0, Math.min(max, Number(hp.hp) || 0));
  const ratio = current / max;
  // Ceil, not round: one sliver of health must never render as an empty bar.
  const filled = current > 0 ? Math.max(1, Math.ceil(ratio * SEGMENTS)) : 0;
  const tone = ratio > 0.5 ? '#5ef38c' : ratio > 0.25 ? '#ffc10a' : '#ff2e4d';
  const low = ratio <= 0.25;

  // The vest. Absent on any level that never sells one — the payload simply has
  // no `armour`, and `> 0` is false for both that and an empty vest.
  const maxArmour = Math.max(1, Number(hp.maxArmour) || 100);
  const armour = Math.max(0, Math.min(maxArmour, Number(hp.armour) || 0));
  const armourRatio = armour / maxArmour;

  return (
    <div className="select-none" style={{ width: `${width}px` }}>
      {armour > 0 ? (
        <div className="mb-1 flex items-center gap-1.5">
          <span className="font-hud text-[10px] font-bold uppercase leading-none tracking-[0.22em] text-[#7fb8ff]">
            Armour
          </span>
          <div className="h-[5px] flex-1 overflow-hidden rounded-[2px] border border-hairline bg-void/80">
            <div
              className="h-full rounded-[1px]"
              style={{
                // Percent, not a segment count: the strip is 200 px of a
                // continuous quantity, and rounding it into chunks would make a
                // vest look full for the first eight points it lost.
                width: `${armourRatio * 100}%`,
                backgroundColor: '#6fa8ff',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
              }}
            />
          </div>
          <span className="gta-outline font-display text-[12px] leading-none text-[#9cc6ff]">
            {Math.round(armour)}
          </span>
        </div>
      ) : null}

      <div className="flex items-end justify-between px-0.5 pb-1">
        <span className="font-hud text-[11px] font-bold uppercase leading-none tracking-[0.22em] text-hud-dim">
          Health
        </span>
        <span
          className="gta-outline font-display text-[19px] leading-none"
          style={{ color: tone }}
        >
          {Math.round(current)}
        </span>
      </div>

      <div className="flex gap-[2px] rounded-[3px] border border-hairline bg-void/80 p-[3px]">
        {Array.from({ length: SEGMENTS }, (_, i) => (
          <div
            key={i}
            className={`h-[9px] flex-1 rounded-[1px] ${low && i < filled ? 'wanted-pulse' : ''}`}
            style={{
              backgroundColor: i < filled ? tone : '#191d26',
              // The lit segments carry a hard inner highlight so the bar keeps
              // its chunky arcade read at 9 px tall.
              boxShadow: i < filled ? 'inset 0 1px 0 rgba(255,255,255,0.28)' : 'none',
            }}
          />
        ))}
      </div>
    </div>
  );
}
